// "PatientImmunisation" -> "Patient Immunisation". The backend sends each mergeable
// DAO's own entity label, which is a class-derived name; this is display formatting
// only and never changes which labels exist.
export const formatRecordLabel = (label) =>
  String(label ?? "").replace(/([a-z0-9])([A-Z])/g, "$1 $2");

// `recordsUpdated` arrives only in the merge response — there is no preview endpoint,
// so this can describe what moved and never what is about to move. Types with no rows
// are omitted by the backend, so an empty map genuinely means nothing was linked.
export const summariseRecordsUpdated = (recordsUpdated) => {
  const rows = Object.entries(recordsUpdated ?? {}).map(([label, count]) => ({
    label: formatRecordLabel(label),
    count,
  }));

  return {
    rows,
    total: rows.reduce((runningTotal, row) => runningTotal + row.count, 0),
  };
};

// Two national identifiers that disagree are the one piece of evidence on this screen
// pointing the other way: everything else here suggests these might be one person,
// a different PPSN or IHI suggests they are two. It warns, it does not block — the
// numbers themselves are sometimes wrong, and the admin is the one deciding.
const IDENTIFIER_FIELDS = [
  { field: "nationalNumber", label: "PPS number" },
  { field: "ihiNumber", label: "IHI number" },
];

export const getIdentifierConflicts = (firstPatient, secondPatient) =>
  IDENTIFIER_FIELDS.filter(({ field }) => {
    const firstValue = (firstPatient?.[field] ?? "").trim();
    const secondValue = (secondPatient?.[field] ?? "").trim();

    // Only a disagreement counts. One record simply not holding the number is the
    // ordinary case for a duplicate and says nothing either way.
    return !!firstValue && !!secondValue && firstValue !== secondValue;
  }).map(({ field, label }) => ({
    label,
    values: [firstPatient[field].trim(), secondPatient[field].trim()],
  }));

export const MERGE_BLOCKER = {
  missingSelection: "Pick both records before merging.",
  missingSurvivor: "Choose which record is kept.",
  samePatient: "Those are the same record. Pick two different patients.",
};

// Which record survives is the whole decision, so it is read from an explicit choice
// and never inferred from the order the two were picked in. `existingPatientId` is the
// record being merged away and `newPatientId` the one kept — the backend repoints every
// row from the first onto the second and then marks the first as merged.
export const getMergePlan = ({ firstPatient, secondPatient, survivorId }) => {
  if (!firstPatient?.id || !secondPatient?.id) {
    return { isReady: false, blocker: MERGE_BLOCKER.missingSelection };
  }

  if (firstPatient.id === secondPatient.id) {
    return { isReady: false, blocker: MERGE_BLOCKER.samePatient };
  }

  const survivor = [firstPatient, secondPatient].find(
    (patient) => patient.id === survivorId
  );

  if (!survivor) {
    return { isReady: false, blocker: MERGE_BLOCKER.missingSurvivor };
  }

  const mergedAway =
    survivor.id === firstPatient.id ? secondPatient : firstPatient;

  return {
    isReady: true,
    blocker: null,
    survivor,
    mergedAway,
    request: { existingPatientId: mergedAway.id, newPatientId: survivor.id },
  };
};
